import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

export type StreamChange = { label: string; before: string; after: string };

export default function StreamChangesDialog({
    open,
    changes,
    busy,
    error,
    onBack,
    onConfirm,
    onExited,
}: {
    open: boolean;
    changes: StreamChange[];
    busy: boolean;
    error: string;
    onBack: () => void;
    onConfirm: () => void;
    onExited: () => void;
}) {
    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onBack}
            fullWidth
            maxWidth="sm"
            TransitionProps={{ onExited }}
            aria-labelledby="stream-changes-title"
            aria-describedby="stream-changes-effect"
            PaperProps={{ sx: { m: { xs: 2, sm: 4 }, width: '100%', borderRadius: 3, bgcolor: 'var(--lm-surface)' } }}
        >
            <DialogTitle
                id="stream-changes-title"
                sx={{ fontSize: 22, fontWeight: 600 }}
            >
                Проверьте изменения
            </DialogTitle>
            <DialogContent>
                <Box
                    component="dl"
                    sx={{ m: 0 }}
                >
                    {changes.map(({ label, before, after }) => (
                        <Box
                            key={label}
                            sx={{ py: 1.5, borderBottom: '1px solid var(--lm-border)' }}
                        >
                            <Typography
                                component="dt"
                                sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
                            >
                                {label}
                            </Typography>
                            <Box
                                component="dd"
                                sx={{
                                    m: 0,
                                    display: 'grid',
                                    gap: 1,
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                }}
                            >
                                {[
                                    [before, 'Было'],
                                    [after, 'Будет'],
                                ].map(([value, title]) => (
                                    <Box
                                        key={title}
                                        sx={{ minWidth: 0 }}
                                    >
                                        <Typography sx={{ fontSize: 12, color: 'var(--lm-muted)', mb: 0.5 }}>
                                            {title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: 'pre-wrap',
                                                overflowWrap: 'anywhere',
                                                color: title === 'Было' ? 'var(--lm-muted)' : 'var(--lm-text)',
                                            }}
                                        >
                                            {value || 'Не указано'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
                <Typography
                    id="stream-changes-effect"
                    sx={{ fontSize: 13, color: 'var(--lm-muted)', mt: 2 }}
                >
                    Описание и локация учитываются при поиске лидов. Автопополнение отключится — его нужно будет включить заново.
                </Typography>
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mt: 2 }}
                    >
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    pb: 2,
                    gap: 1,
                    flexWrap: 'wrap',
                    '& .MuiButton-root': { minHeight: 44, fontFamily: 'inherit' },
                }}
            >
                <Button
                    disabled={busy}
                    onClick={onBack}
                >
                    Вернуться
                </Button>
                <Button
                    variant="contained"
                    disabled={busy}
                    onClick={onConfirm}
                >
                    {busy ? 'Сохраняем…' : 'Сохранить изменения'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
